import { useCallback } from 'react';
import { Flex, Text, Button, Inset } from '@radix-ui/themes';
import { Dialog } from '@/components/shared/dialog';
import styles from './OnboardingModal.module.scss';
import { AnalyticsIcon } from '@/v2/assets/icons/AnalyticsIcon';
import { AiMagicIcon } from '@/v2/assets/icons/AiMagicIcon';
import AiContentGenerator01 from '@/v2/assets/icons/AiContentGenerator01';
import { CheckmarkCircle01Icon } from '@/v2/assets/icons/CheckmarkCircle01';
import { DEFAULT_ONBOARDING_CONFIG } from './OnboardingModal.types';

interface StripeRedirectModalProps {
  isOpen: boolean;
  onRedirect: () => void;
}

export function StripeRedirectModal({ isOpen, onRedirect }: StripeRedirectModalProps) {
  const handleRedirect = useCallback(() => {
    onRedirect();
  }, [onRedirect]);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <Dialog.Content size="large" className={styles.modalContent}>
        <Flex height="100%" width="100%" style={{ height: '100%', minHeight: '100%' }}>
          <Flex direction="column" className={styles.leftPanel}>
            <Flex direction="column" className={styles.formHeader}>
              <Text className={styles.mainTitle}>
                Start your free trial today!
              </Text>
              <Text size="3" className={styles.subtitle}>
                Here's a peek of what you get with Quack
              </Text>
            </Flex>

            <Flex direction="column" gap="5" className={styles.featuresSection}>
              <Flex gap="3" align="start">
                <div className={styles.featureIcon}>
                  <AiMagicIcon size={24} fill='orange' />
                </div>
                <Flex direction="column" gap="1">
                  <Text size="4" weight="bold" className={styles.featureTitle}>
                    Unlimited AI agents
                  </Text>
                  <Text size="3" className={styles.featureDescription}>
                    Create, deploy, and customize as many AI agents as you need — no limits, no complexity.
                  </Text>
                </Flex>
              </Flex>

              <Flex gap="3" align="start">
                <div className={styles.featureIcon}>
                  <AiContentGenerator01 fill='teal' size={24} />
                </div>
                <Flex direction="column" gap="1">
                  <Text size="4" weight="bold" className={styles.featureTitle}>
                    Knowledge Management
                  </Text>
                  <Text size="3" className={styles.featureDescription}>
                    Centralize your content and keep every answer accurate, consistent, and up to date.
                  </Text>
                </Flex>
              </Flex>

              <Flex gap="3" align="start">
                <div className={styles.featureIcon}>
                  <AnalyticsIcon size={24} fill='purple' />
                </div>
                <Flex direction="column" gap="1">
                  <Text size="4" weight="bold" className={styles.featureTitle}>
                    VoC Dashboards & Reports
                  </Text>
                  <Text size="3" className={styles.featureDescription}>
                    Track performance, spot trends, and understand what customers really need in real time.
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            <Flex direction="column" gap="3" mt="6" className={styles.trialSection}>
              <Text size="4" weight="bold" className={styles.trialTitle}>
                Start your 14 days free trial
              </Text>
              <Flex direction="column" gap="2">
                <Flex gap="2" align="center">
                  <CheckmarkCircle01Icon />
                  <Text size="3" className={styles.trialBenefit}>
                    Get full access to every feature
                  </Text>
                </Flex>
                <Flex gap="2" align="center">
                  <CheckmarkCircle01Icon />
                  <Text size="3" className={styles.trialBenefit}>
                    Cancel anytime
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            <Flex gap="3" mt="6">
              <Button variant="solid" color="gray" onClick={handleRedirect} size="4" highContrast className={styles.ctaButton}>
                Try Quack for free
              </Button>
            </Flex>
          </Flex>
          
          <Flex className={styles.rightPanel}>
              <img src={DEFAULT_ONBOARDING_CONFIG.backgroundImageUrl} alt="Background" className={styles.backgroundImage} />
          </Flex>

        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}

